import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { BrandIcon } from "@/shared/components/BrandIcon";

const PasswordReset = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingSession, setVerifyingSession] = useState(true);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [pendingTokenHash, setPendingTokenHash] = useState<string | null>(null);
  const [pendingTokenType, setPendingTokenType] = useState<'recovery' | 'signup' | 'invite' | 'magiclink' | 'email_change' | 'email'>('recovery');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const hashType = hashParams.get('type');
    const tokenHash = queryParams.get('token_hash') || hashParams.get('token_hash');
    const tokenType = (queryParams.get('type') || hashType || 'recovery') as typeof pendingTokenType;
    const errorParam = hashParams.get('error') || queryParams.get('error');
    const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');

    if (errorParam || errorDescription) {
      const errorMessage = errorDescription
        ? (errorParam ? `${errorParam}: ${errorDescription}` : errorDescription)
        : `Auth error: ${errorParam}`;
      setError(errorMessage);
      setHasValidSession(false);
      setVerifyingSession(false);
      return;
    }

    const setSessionFromUrlTokens = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken ?? '',
          refresh_token: refreshToken ?? ''
        });

        if (sessionError) {
          throw sessionError;
        }

        if (!data.session) {
          throw new Error('No session returned from token exchange.');
        }

        setHasValidSession(true);
      } catch (err: any) {
        setError(err.message || 'Unable to verify recovery session. Please request a new password reset link.');
        setHasValidSession(false);
      } finally {
        setVerifyingSession(false);
      }
    };

    const verifyExistingSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          throw sessionError;
        }

        if (!session) {
          throw new Error('No valid recovery session found. Please click the reset link from your email.');
        }

        setHasValidSession(true);
      } catch (err: any) {
        setError(err.message || 'Unable to verify recovery session. Please request a new password reset link.');
        setHasValidSession(false);
      } finally {
        setVerifyingSession(false);
      }
    };

    if (tokenHash) {
      setPendingTokenHash(tokenHash);
      setPendingTokenType(tokenType);
      setRequiresConfirmation(true);
      setVerifyingSession(false);
      return;
    }

    if (accessToken && refreshToken) {
      setSessionFromUrlTokens();
      return;
    }

    verifyExistingSession();
  }, []);

  const handleVerifyToken = async () => {
    if (!pendingTokenHash) {
      setError('Missing recovery token. Please request a new password reset link.');
      setRequiresConfirmation(false);
      setHasValidSession(false);
      return;
    }

    setVerifyingToken(true);
    setError(null);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        type: pendingTokenType,
        token_hash: pendingTokenHash
      });

      if (verifyError) {
        throw verifyError;
      }

      if (!data.session) {
        throw new Error('No session returned from token verification.');
      }

      setHasValidSession(true);
      setRequiresConfirmation(false);
      setPendingTokenHash(null);
      setPendingTokenType('recovery');

      const url = new URL(window.location.href);
      url.searchParams.delete('token_hash');
      url.searchParams.delete('type');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    } catch (err: any) {
      setError(err.message || 'Unable to verify recovery link. Please request a new password reset link.');
      setHasValidSession(false);
      setRequiresConfirmation(false);
    } finally {
      setVerifyingToken(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        throw updateError;
      }

      setMessage('Password updated successfully! Redirecting...');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  if (verifyingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-sm shadow-elevated">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Verifying recovery session...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (requiresConfirmation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-3 mb-8">
            <BrandIcon className="h-9 w-9" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Factory Pilot</h1>
            </div>
          </div>

          <Card className="shadow-elevated">
            <CardHeader className="text-center">
              <CardTitle>Confirm reset link</CardTitle>
              <CardDescription>Press continue to verify your password reset request.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button onClick={handleVerifyToken} className="w-full" variant="industrial" disabled={verifyingToken}>
                {verifyingToken ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
              <Button variant="outline" onClick={() => navigate('/forgot-password')} className="w-full">
                Request new reset link
              </Button>
              <Button variant="outline" onClick={() => navigate('/auth')} className="w-full">
                Back to sign in
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!hasValidSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-3 mb-8">
            <BrandIcon className="h-9 w-9" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Factory Pilot</h1>
            </div>
          </div>

          <Card className="shadow-elevated">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Invalid recovery link</CardTitle>
              <CardDescription>Unable to verify your password reset request.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <p className="text-sm text-muted-foreground text-center">
                The password reset link may have expired or is invalid. Please request a new one.
              </p>
              <Button onClick={() => navigate('/forgot-password')} className="w-full" variant="industrial">
                Request new reset link
              </Button>
              <Button variant="outline" onClick={() => navigate('/auth')} className="w-full">
                Back to sign in
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <BrandIcon className="h-9 w-9" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Factory Pilot</h1>
          </div>
        </div>

        <Card className="shadow-elevated">
          <CardHeader className="text-center">
            <CardTitle>Update your password</CardTitle>
            <CardDescription>Enter a new password for your account.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdate} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {message && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" variant="industrial" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating password...
                  </>
                ) : (
                  'Update password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PasswordReset;
